import { CommonModule, NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MediaType, VideoDurationRange } from "@front/m19-api-client";
import { formatDurationRanges, rangesContain } from "@front/m19-services";
import {
  IAlertComponent,
  IButtonDirective,
  IIconComponent,
  ITab,
  ITabsComponent,
  ITooltipDirective,
  MODAL_DATA,
  ModalOptions,
  ModalRef,
  ModalService,
  SpinnerComponent,
} from "@front/m19-ui";
import { readVideoDurationSeconds } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { MediaImageSize } from "@m19-board/dsp/dsp-creatives/dsp-custom-creatives/dsp-custom-creatives-table/dsp-custom-creatives-lines.component";
import { Media } from "@m19-board/shared/media/media";
import {
  MediaPreviewModalComponent,
  MediaPreviewModalData,
} from "@m19-board/shared/media/media-preview-modal/media-preview-modal.component";
import { MediaSelectionComponent } from "@m19-board/shared/media/media-selection/media-selection.component";
import { isVideo } from "@m19-board/shared/media/media.utils";
import { catchError, forkJoin, map, Observable, of, take } from "rxjs";

enum MediaInputTab {
  MEDIA,
  UPLOAD_NEW,
}

type Resolution = readonly [width: number, height: number];

interface VideoConstraints {
  authorizedResolutions: readonly Resolution[];
  minDurationSeconds: number;
  maxDurationSeconds: number;
}

interface MediaConstraints {
  maxSizeBytes: number;
  minResolution?: Resolution;
  video?: VideoConstraints;
}

const VIDEO_RESOLUTIONS: readonly Resolution[] = [
  [1920, 1080],
  [1280, 720],
  [3840, 2160],
];
const DSP_VIDEO_RESOLUTIONS: readonly Resolution[] = [[1920, 1080]];

const VIDEO_DURATION_TOLERANCE_SECONDS = 1;

const formatMegabytes = (bytes: number): string => `${bytes / 1_000_000}MB`;

const formatAlternatives = (values: string[], lang: string): string =>
  new Intl.ListFormat(lang, { type: "disjunction" }).format(values);

const formatResolution = ([width, height]: Resolution): string => `${width}x${height}`;

const hasAuthorizedResolution = (video: HTMLVideoElement, authorized: readonly Resolution[]): boolean =>
  authorized.some(
    ([width, height]) =>
      (video.videoWidth === width && video.videoHeight === height) ||
      (video.videoHeight === width && video.videoWidth === height),
  );

@Component({
  selector: "app-media-selection-modal",
  imports: [
    CommonModule,
    IButtonDirective,
    ITooltipDirective,
    ITabsComponent,
    MediaSelectionComponent,
    TranslocoDirective,
    IAlertComponent,
    IIconComponent,
    NgTemplateOutlet,
    SpinnerComponent,
  ],
  templateUrl: "./media-selection-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaSelectionModalComponent<T extends Media> {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "lg" };

  private readonly translocoService = inject(TranslocoService);
  protected readonly modalRef = inject<ModalRef<MediaSelectionModalResult<T>>>(ModalRef);
  protected readonly modalData = inject<MediaSelectionModalData>(MODAL_DATA);
  private readonly modalService = inject(ModalService);

  protected readonly MediaInputTab = MediaInputTab;

  private readonly constraints = computed<MediaConstraints | undefined>(() => {
    switch (this.modalData.mediaType) {
      case MediaType.brandLogo:
        return { maxSizeBytes: 1_000_000, minResolution: [400, 400] };
      case MediaType.customImage:
        return { maxSizeBytes: 5_000_000, minResolution: [1200, 628] };
      case MediaType.videoAsset:
        return {
          maxSizeBytes: 300_000_000,
          video: {
            authorizedResolutions: this.modalData.isDsp ? DSP_VIDEO_RESOLUTIONS : VIDEO_RESOLUTIONS,
            minDurationSeconds: 6,
            maxDurationSeconds: 45,
          },
        };
      case MediaType.otherImage:
        return {
          maxSizeBytes: 5_000_000,
          minResolution: undefined,
        };
      default:
        return undefined;
    }
  });

  isVideo = computed(() => isVideo(this.modalData.mediaType));

  readonly hasDurationFilter = computed(
    () => this.isVideo() && (this.modalData.allowedVideoDurations?.length ?? 0) > 0,
  );
  readonly allowedDurationsLabel = computed(() => formatDurationRanges(this.modalData.allowedVideoDurations ?? []));

  private readonly mediasOfType = this.modalData.medias.filter((m) => m.mediaType === this.modalData.mediaType);

  // Durations are not stored on the asset, so read them client-side for the video medias to filter.
  // Stays `undefined` until the read resolves, which drives the loading spinner.
  private readonly measuredDurations = toSignal(this.readDurations(), { initialValue: undefined });

  private readDurations(): Observable<Map<string, number | undefined>> {
    if (!this.hasDurationFilter() || !this.modalData.dspAdvertiserId) return of(new Map<string, number | undefined>());
    const withUrl = this.mediasOfType.filter((m) => m.url);
    if (withUrl.length === 0) return of(new Map<string, number | undefined>());
    return forkJoin(
      withUrl.map((m) =>
        readVideoDurationSeconds(m.url!).pipe(
          take(1),
          catchError(() => of(undefined)),
          map((duration) => [m.url!, duration] as const),
        ),
      ),
    ).pipe(map((entries) => new Map<string, number | undefined>(entries)));
  }

  readonly durationsLoading = computed(() => this.hasDurationFilter() && this.measuredDurations() === undefined);

  readonly medias = computed(() => {
    const medias = this.mediasOfType;
    if (!this.hasDurationFilter()) return medias;
    const durations = this.measuredDurations();
    if (!durations) return []; // still reading durations; the spinner is shown instead of the list
    const allowed = this.modalData.allowedVideoDurations ?? [];
    // Keep a video unless its duration is known and not among the allowed durations (unreadable
    // durations stay visible so a valid asset is never silently hidden).
    return medias.filter((m) => {
      const duration = m.url ? durations.get(m.url) : undefined;
      return duration === undefined || rangesContain(allowed, duration);
    });
  });

  readonly mediaConstraintsTip = computed(() => {
    const constraints = this.constraints();
    if (!constraints) return "";
    const maxSize = formatMegabytes(constraints.maxSizeBytes);

    const video = constraints.video;
    if (video) {
      return this.translocoService.translate("media-input.video_constraints_tip", {
        maxSize,
        resolutions: formatAlternatives(
          video.authorizedResolutions.map(formatResolution),
          this.translocoService.getActiveLang(),
        ),
        minDuration: video.minDurationSeconds,
        maxDuration: video.maxDurationSeconds,
      });
    }

    const minResolution = constraints.minResolution;
    if (minResolution) {
      return this.translocoService.translate("media-input.image_constraints_tip", {
        maxSize,
        minWidth: minResolution[0],
        minHeight: minResolution[1],
      });
    }
    return "";
  });

  isValidMedia?: boolean;
  invalidReason = signal("");

  newMediaFile?: File;

  media = linkedSignal({
    source: () => (this.isVideo() ? (document.createElement("video") as HTMLVideoElement) : new Image()),
    computation: (source) => source,
  });

  selectedMedia?: T;

  readonly tabs: ITab<MediaInputTab>[] = [
    { value: MediaInputTab.MEDIA, label: this.translocoService.translate("media-input.medias") },
    { value: MediaInputTab.UPLOAD_NEW, label: this.translocoService.translate("media-input.upload_new_file") },
  ];

  setIsValidMediaFile(isValid: boolean | undefined) {
    this.isValidMedia = isValid;
  }

  saveMedia(media: T | undefined) {
    this.newMediaFile = undefined;
    this.modalRef.close({
      newFile: undefined,
      mediaSrc: undefined,
      selectedMedia: media,
    });
  }

  saveNewFile(file: File | undefined) {
    this.modalRef.close({
      newFile: file,
      mediaSrc: this.media().src,
      selectedMedia: undefined,
    });
  }

  onNewMedia(files: FileList | null) {
    if (files && files.length > 0) {
      this.invalidReason.set("");
      this.selectedMedia = undefined;
      this.isValidMedia = true;
      this.newMediaFile = files[0];

      if (!this.isFileValid(this.newMediaFile)) {
        this.media().src = "";
        this.setIsValidMediaFile(false);
        return;
      }

      const reader = new FileReader();
      // read as dataURL to render and check resolution
      reader.readAsDataURL(this.newMediaFile);
      reader.onload = () => {
        const media = this.media();
        if (this.isVideo()) {
          (media as HTMLVideoElement).onloadedmetadata = (event) => {
            const isValid = this.checkMediaResolution(media);
            this.setIsValidMediaFile(isValid);
            if (!isValid) {
              const video = media as HTMLVideoElement;
              this.invalidReason.set(
                this.translocoService.translate("media-input.invalid_video_resolution_or_duration", {
                  resolution: formatResolution([video.videoWidth, video.videoHeight]),
                  duration: video.duration.toFixed(1),
                }),
              );
            }
          };
          (media as HTMLVideoElement).onerror = (event) => {
            // not able to read video on client browser (codec issue)
            // let's try with amazon
            this.setIsValidMediaFile(true);
          };
          media.src = reader.result as string;
          (media as HTMLVideoElement).load();
        } else {
          media.src = reader.result as string;
          media.onload = () => {
            const isValid = this.checkMediaResolution(media);
            this.setIsValidMediaFile(isValid);
            if (!isValid) {
              this.invalidReason.set(
                this.translocoService.translate("media-input.invalid_image_resolution", {
                  resolution: formatResolution([media.width, media.height]),
                }),
              );
            }
          };
        }
        this.media.set(media);
      };
    }
  }

  private isFileValid(file: File): boolean {
    if (!this.isValidFileSize(file.size)) {
      this.invalidReason.set(this.translocoService.translate("media-input.invalid_file_size"));
      return false;
    }

    if (!this.isValidFileType(file.type)) {
      this.invalidReason.set(this.translocoService.translate("media-input.invalid_file_type"));
      return false;
    }
    return true;
  }

  private isValidFileSize(size: number): boolean {
    const maxSizeBytes = this.constraints()?.maxSizeBytes;
    return maxSizeBytes === undefined || size <= maxSizeBytes;
  }

  private isValidFileType(type: string) {
    switch (this.modalData.mediaType) {
      case MediaType.brandLogo:
      case MediaType.customImage:
      case MediaType.otherImage:
        return ["image/gif", "image/jpeg", "image/png"].includes(type);
      case MediaType.videoAsset:
        return type.startsWith("video/");
      default:
        return true;
    }
  }

  checkMediaResolution(media: HTMLVideoElement | HTMLImageElement): boolean {
    const constraints = this.constraints();
    if (!constraints) return false;

    const video = constraints.video;
    if (video) {
      const videoMedia = media as HTMLVideoElement;
      return (
        hasAuthorizedResolution(videoMedia, video.authorizedResolutions) &&
        videoMedia.duration >= video.minDurationSeconds &&
        videoMedia.duration <= video.maxDurationSeconds + VIDEO_DURATION_TOLERANCE_SECONDS
      );
    }

    const minResolution = constraints.minResolution;
    if (minResolution) {
      return media.width >= minResolution[0] && media.height >= minResolution[1];
    }
    return true;
  }

  openMediaToPreview(mediaToPreview: Media | undefined) {
    this.modalService.openModal<MediaPreviewModalData, void>(MediaPreviewModalComponent, {
      modalTitle: mediaToPreview ? mediaToPreview.name : this.newMediaFile?.name,
      data: {
        mediaSource: mediaToPreview ? mediaToPreview.url! : this.media().src!,
        mediaType: this.modalData.mediaType,
      },
    });
  }

  selectedTab: MediaInputTab = MediaInputTab.MEDIA;
}

export interface MediaSelectionModalData {
  medias: Media[];
  mediaType: MediaType;
  disableUpload?: boolean;
  isDsp?: boolean;
  allowedVideoDurations?: VideoDurationRange[];
  // When set, video durations are read via the advertiser/asset-cached accessor.
  dspAdvertiserId?: string;
  imageSizeFilterValues?: MediaImageSize[];
}

export interface MediaSelectionModalResult<T extends Media> {
  newFile?: File;
  mediaSrc?: string;
  selectedMedia?: T;
}
